import { format } from 'date-fns';

import type { ValueOfObject } from '@app-types/global';

const DateFormats = {
  RegularDate: 'dd/MM/yyyy' as const,
  RegularDateTime: 'MM/dd/yyyy H:mm:ss' as const,
  MonthAndYear: 'MM/yyyy' as const,
  SnakeMonthAndYear: 'MM-yyyy' as const,
  DayMonthAndYear: 'MM/dd/yyyy' as const,
  YearMonthAndDay: 'yyyy-MM-dd' as const,
  DayMonth: 'dd/MM' as const,
  RegularFormatISO: "yyyy-MM-dd'T'HH:mm:ss" as const,
  RegularTime: 'HH:mm:ss' as const,
};

const formatDateAs = (dateObject: Date | string, formatDate: ValueOfObject<typeof DateFormats>) => {
  if (!dateObject) {
    return '-';
  }
  return format(new Date(dateObject), formatDate);
};

/**
 * Formats an epoch-millisecond timestamp as an explicit UTC wall-clock string.
 *
 * Ops runs on UTC, so timestamps are rendered in UTC and labelled as such rather than converted to
 * the viewer's local zone — mixing the two is how people misread a deadline.
 *
 * Deliberately not routed through `formatDateAs`: that helper calls `new Date(value)`, and for a
 * zone-less string such as `"2026-08-05 10:36:38"` V8 applies the *local* timezone, silently shifting
 * the value by the viewer's offset. Taking an epoch number sidesteps the ambiguity entirely.
 *
 * @param epochMs epoch milliseconds (UTC), or null/undefined when the source had no timestamp
 * @returns e.g. `2026-08-05 10:36:38 UTC`, or `-` when absent
 */
const formatEpochAsUtc = (epochMs?: number | null): string => {
  if (epochMs === null || epochMs === undefined) {
    return '-';
  }
  return `${new Date(epochMs).toISOString().slice(0, 19).replace('T', ' ')} UTC`;
};

export { formatDateAs, formatEpochAsUtc, DateFormats };
